import React, { useState } from "react";

import { IDog, IDogRun } from "@eagerdog/interfaces";

import Input from "../../../components/Input/Input";
import Button from "../../../components/Button/Button";

import "./NoseworkRunForm.scss";

interface IProps {
  dog: IDog;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  editRun?: IDogRun;
}

const LEVELS = [
  { value: 'Novice', label: 'Novice' },
  { value: 'Advanced', label: 'Advanced' },
  { value: 'Superior', label: 'Superior' },
  { value: 'Master', label: 'Master' },
  { value: 'Elite', label: 'Elite' }
];

// Handler Discrimination has different levels
const HD_LEVELS = [
  { value: 'Novice', label: 'Novice' },
  { value: 'Advanced', label: 'Advanced' },
  { value: 'Excellent', label: 'Excellent' },
  { value: 'Master', label: 'Master' }
];

const ELEMENTS = [
  { value: 'Container', label: 'Container' },
  { value: 'Exterior', label: 'Exterior' },
  { value: 'Interior', label: 'Interior' },
  { value: 'Vehicle', label: 'Vehicle' },
  { value: 'Handler Discrimination', label: 'Handler Discrimination' }
];

const DIVISIONS = [
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' }
];

const RESULT_STATUSES = [
  { value: 'Q', label: 'Q - Qualified' },
  { value: 'NQ', label: 'NQ - Not Qualified' },
  { value: 'ABS', label: 'ABS - Absent' },
  { value: 'DQ', label: 'DQ - Disqualified' },
  { value: 'EXC', label: 'EXC - Excused' }
];

const NoseworkRunForm: React.FC<IProps> = ({ dog, onSubmit, onCancel, isSubmitting, editRun }) => {
  // Form fields
  const [trialDate, setTrialDate] = useState<string>(
    editRun?.trial_date ? new Date(editRun.trial_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
  );
  const [element, setElement] = useState<string>(editRun?.element || "Container");
  const [level, setLevel] = useState<string>(editRun?.level || "Novice");
  const [division, setDivision] = useState<string>(editRun?.division || "A");
  const [resultStatus, setResultStatus] = useState<string>(editRun?.result_status || "Q");
  const [eventName, setEventName] = useState<string>(editRun?.event_name || "");
  const [city, setCity] = useState<string>(editRun?.location?.city || "");
  const [state, setState] = useState<string>(editRun?.location?.state || "");
  const [notes, setNotes] = useState<string>(editRun?.notes || "");

  // Validation errors
  const [errors, setErrors] = useState<any>({});
  
  // Determine which levels to show based on element
  const availableLevels = element === 'Handler Discrimination' ? HD_LEVELS : LEVELS;
  
  // When element changes, validate/adjust level if needed
  const handleElementChange = (newElement: string) => {
    setElement(newElement);
    
    // If switching to/from Handler Discrimination, reset level if current one isn't valid
    if (newElement === 'Handler Discrimination') {
      // If current level is Superior or Elite, reset to Novice
      if (level === 'Superior' || level === 'Elite') {
        setLevel('Novice');
      }
    } else if (element === 'Handler Discrimination') {
      // If switching away from HD and current level is Excellent, reset to Novice
      if (level === 'Excellent') {
        setLevel('Novice');
      }
    }
  };

  const validate = () => {
    const newErrors: any = {};

    if (!trialDate) {
      newErrors.trialDate = "Trial date is required";
    } else if (new Date(trialDate) > new Date()) {
      newErrors.trialDate = "Trial date cannot be in the future";
    }

    if (!level) {
      newErrors.level = "Level is required";
    }

    if (!element) {
      newErrors.element = "Element is required";
    }

    if (!division) {
      newErrors.division = "Division is required";
    }

    if (!resultStatus) {
      newErrors.resultStatus = "Result status is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Parse the date and set it to noon local time to avoid timezone issues
    const [year, month, day] = trialDate.split('-').map(Number);
    const localDate = new Date(year, month - 1, day, 12, 0, 0);

    const formData: any = {
      dog_id: dog._id,
      level,
      element,
      division,
      result_status: resultStatus,
      trial_date: localDate,
      event_name: eventName || undefined,
      location: (city || state) ? {
        city: city || undefined,
        state: state || undefined
      } : undefined,
      notes: notes || undefined
    };

    // If editing, include the run ID
    if (editRun) {
      formData.run_id = editRun._id;
    }

    onSubmit(formData);
  };

  return (
    <form className="noseworkRunForm" onSubmit={handleSubmit}>
      <div className="formSection">
        <h3>Run Details</h3>
        
        <div className="formGroup">
          <label htmlFor="trialDate">Trial Date *</label>
          <Input
            id="trialDate"
            type="date"
            value={trialDate}
            onChange={(e) => setTrialDate(e.target.value)}
            required
            max={new Date().toISOString().split('T')[0]}
          />
          {errors.trialDate && <div className="fieldError">{errors.trialDate}</div>}
        </div>

        <div className="formRow">
          <div className="formGroup">
            <label htmlFor="element">Element *</label>
            <select
              id="element"
              value={element}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleElementChange(e.target.value)}
              required
            >
              {ELEMENTS.map(el => (
                <option key={el.value} value={el.value}>{el.label}</option>
              ))}
            </select>
            {errors.element && <div className="fieldError">{errors.element}</div>}
          </div>

          <div className="formGroup">
            <label htmlFor="level">Level *</label>
            <select
              id="level"
              value={level}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setLevel(e.target.value)}
              required
            >
              {availableLevels.map(l => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
            {errors.level && <div className="fieldError">{errors.level}</div>}
          </div>
        </div>

        <div className="formRow">
          <div className="formGroup">
            <label htmlFor="division">Division *</label>
            <select
              id="division"
              value={division}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setDivision(e.target.value)}
              required
            >
              {DIVISIONS.map(d => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
            {errors.division && <div className="fieldError">{errors.division}</div>}
            <div className="fieldHelp">
              A: Handler has not earned a title in this level<br/>
              B: Handler has earned a title in this level
            </div>
          </div>

          <div className="formGroup">
            <label htmlFor="resultStatus">Result *</label>
            <select
              id="resultStatus"
              value={resultStatus}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setResultStatus(e.target.value)}
              required
            >
              {RESULT_STATUSES.map(rs => (
                <option key={rs.value} value={rs.value}>{rs.label}</option>
              ))}
            </select>
            {errors.resultStatus && <div className="fieldError">{errors.resultStatus}</div>}
          </div>
        </div>
      </div>

      <div className="formSection">
        <h3>Event Information (Optional)</h3>
        
        <div className="formGroup">
          <label htmlFor="eventName">Event Name</label>
          <Input
            id="eventName"
            type="text"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            placeholder="e.g., Spring Nosework Trial"
          />
        </div>

        <div className="formRow">
          <div className="formGroup">
            <label htmlFor="city">City</label>
            <Input
              id="city"
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="City"
            />
          </div>

          <div className="formGroup">
            <label htmlFor="state">State</label>
            <Input
              id="state"
              type="text"
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder="State"
              maxLength={2}
            />
          </div>
        </div>

        <div className="formGroup">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any additional notes about this run..."
            rows={3}
          />
        </div>
      </div>

      <div className="formActions">
        <Button
          type="button"
          onClick={onCancel}
          variant="secondary"
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving..." : editRun ? "Update Run" : "Add Run"}
        </Button>
      </div>
    </form>
  );
};

export default NoseworkRunForm;

